/**
 * Saved-route schema + migrator.
 *
 * Routes are persisted to `currentUser.privateData.savedRoutes` (max 5).
 * Polyline / distance / duration are NEVER written here — they live in the
 * per-device polylineCache and are recomputed from Mapbox Directions on load
 * when the cache is cold.
 *
 * Schema forward compatibility: every saved route carries a `schemaVersion`.
 * `migrateRoute` is the ONLY read path — call it on anything coming out of
 * the profile before trusting the shape.
 */

export const CURRENT_SCHEMA_VERSION = 2;

export const MAX_SAVED_ROUTES = 5;

export const DEFAULT_ROUTE_PREFERENCES = {
  avoidMotorway: false,
  avoidTolls: false,
};

// Sharetribe `privateData` total budget is ~50 KB across the whole profile.
// We reserve ~40 KB for everything else (bookmarks etc.) and give saved
// routes a 10 KB envelope — 2 KB per route at the 5-route cap.
export const SAVED_ROUTES_SIZE_BUDGET_BYTES = 10_000;

/**
 * Coerce a raw saved-route entry (as read from privateData) into the current
 * schema shape. Unknown / missing fields fall back to safe defaults, invalid
 * shapes return null so the caller can drop them rather than crashing the UI.
 *
 * @param {any} raw
 * @returns {import('../../../types/vansite').VanSite.RoutePlanner.SavedRoute | null}
 */
export const migrateRoute = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const version = Number(raw.schemaVersion) || 1;
  if (version > CURRENT_SCHEMA_VERSION) {
    // Written by a newer client. Safer to ignore than to partially parse.
    return null;
  }

  const waypoints = Array.isArray(raw.waypoints)
    ? raw.waypoints
        .map((wp) => {
          if (!wp || typeof wp !== 'object') return null;
          const lat = Number(wp.lat);
          const lng = Number(wp.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return {
            id: String(wp.id || ''),
            label: String(wp.label || ''),
            lat,
            lng,
          };
        })
        .filter(Boolean)
    : [];

  const itinerary = Array.isArray(raw.itinerary)
    ? raw.itinerary
        .map((stop) => {
          if (!stop || typeof stop !== 'object') return null;
          if (!stop.listingId) return null;
          return {
            stopId: String(stop.stopId || ''),
            listingId: String(stop.listingId),
            startDate: stop.startDate || null,
            endDate: stop.endDate || null,
          };
        })
        .filter(Boolean)
    : [];

  const corridorWidthKm = Number(raw.corridorWidthKm);
  const rawPreferences = raw.routePreferences || {};
  const routePreferences = {
    avoidMotorway: Boolean(rawPreferences.avoidMotorway),
    avoidTolls: Boolean(rawPreferences.avoidTolls),
  };

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: String(raw.id || ''),
    name: String(raw.name || ''),
    createdAt: Number(raw.createdAt) || Date.now(),
    updatedAt: Number(raw.updatedAt) || Date.now(),
    corridorWidthKm: Number.isFinite(corridorWidthKm) ? corridorWidthKm : 15,
    routePreferences,
    waypoints,
    itinerary,
  };
};

/**
 * Tight structural validation, used BEFORE writing a route to the profile so
 * we never persist a half-built shape. Returns `null` when valid or a
 * translation key string describing the first problem.
 *
 * @param {any} route
 * @returns {string | null}
 */
export const validateRoute = (route) => {
  if (!route || typeof route !== 'object') return 'RoutePlannerPage.saveError.invalid';
  if (!route.id) return 'RoutePlannerPage.saveError.invalid';
  const name = typeof route.name === 'string' ? route.name.trim() : '';
  if (!name) return 'RoutePlannerPage.saveError.emptyName';
  if (name.length > 60) return 'RoutePlannerPage.saveError.nameTooLong';
  if (!Array.isArray(route.waypoints) || route.waypoints.length < 2) {
    return 'RoutePlannerPage.saveError.notEnoughWaypoints';
  }
  return null;
};

/**
 * Rough size estimate for size-budget checks. Uses JSON string length as a
 * proxy — it's O(route size) and the Sharetribe-side limits are measured in
 * JSON bytes anyway.
 *
 * @param {any} value
 * @returns {number}
 */
export const estimateSizeBytes = (value) => {
  try {
    return new Blob([JSON.stringify(value)]).size;
  } catch {
    return JSON.stringify(value || '').length;
  }
};

/**
 * Returns true if the given list of routes (after adding/replacing `newRoute`)
 * still fits under the size budget.
 *
 * @param {any[]} existingRoutes
 * @param {any} newRoute
 * @returns {boolean}
 */
export const canFitInProfile = (existingRoutes, newRoute) => {
  const without = (existingRoutes || []).filter((r) => r && r.id !== newRoute?.id);
  const next = newRoute ? [...without, newRoute] : without;
  return estimateSizeBytes(next) <= SAVED_ROUTES_SIZE_BUDGET_BYTES;
};
